////////////////////////////////////////
// # GOLDEN RULE!!!! HONOR ABOVE ALL!!!!
////////////////////////////////////////
// NO NEW FILES!!!!!!!!!
////////////////////////////////////////

// BoredAndAmbitious_Home_SRC_Homepage_Lobby_Pod.tsx
// UI Components for B&A Homepage

/*
═══════════════════════════════════════════════════════════════════════════════════════
  IMPORTS
═══════════════════════════════════════════════════════════════════════════════════════
*/

import React from 'react';
import { useHomepageState } from '../pea/BoredAndAmbitious_Home_SRC_Homepage_Lobby_Pea.js';
import type { Episode, FeatureCard } from '../pea/BoredAndAmbitious_Home_SRC_Homepage_Lobby_Pea.js';
import styles from '../pea/BoredAndAmbitious_Home_SRC_Homepage_Lobby_Pea.module.css';

/*
═══════════════════════════════════════════════════════════════════════════════════════
  COMPONENTS - Header/Navigation
═══════════════════════════════════════════════════════════════════════════════════════
*/

function Header(): React.ReactElement {
  const { navigation, isMenuOpen, toggleMenu, socialLinks } = useHomepageState();

  return (
    <header className={styles.header}>
      <div className={styles.headerInner}>
        {/* Logo */}
        <a href="/" className={styles.logo}>
          <span className={styles.logoMain}>B&A</span>
          <span className={styles.logoTagline}>Bored & Ambitious</span>
        </a>

        {/* Desktop Navigation */}
        <nav className={styles.desktopNav}>
          {navigation.map((item) => (
            <a key={item.href} href={item.href} className={styles.navLink}>
              {item.label}
            </a>
          ))}
          {/* Listen Dropdown */}
          <div className={styles.listenDropdown}>
            <span className={styles.navLink}>Listen ▾</span>
            <div className={styles.listenDropdownContent}>
              {socialLinks.map((link) => (
                <a
                  key={link.platform}
                  href={link.url}
                  className={styles.listenDropdownLink}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {link.platform === 'spotify' && '♫ '}
                  {link.platform === 'apple' && '🎙️ '}
                  {link.platform === 'amazon' && '🎧 '}
                  {link.platform === 'audible' && '📖 '}
                  {link.label}
                </a>
              ))}
            </div>
          </div>
        </nav>

        {/* Mobile Menu Button */}
        <button
          className={styles.menuButton}
          onClick={toggleMenu}
          aria-label="Toggle menu"
          aria-expanded={isMenuOpen}
        >
          <span className={`${styles.menuIcon} ${isMenuOpen ? styles.menuIconOpen : ''}`} />
        </button>
      </div>

      {/* Mobile Navigation */}
      {isMenuOpen && (
        <nav className={styles.mobileNav}>
          {navigation.map((item) => (
            <a key={item.href} href={item.href} className={styles.mobileNavLink}>
              {item.label}
            </a>
          ))}
          <div className={styles.mobileListenSection}>
            <span className={styles.mobileListenTitle}>Listen On:</span>
            {socialLinks.map((link) => (
              <a
                key={link.platform}
                href={link.url}
                className={styles.mobileNavLink}
                target="_blank"
                rel="noopener noreferrer"
              >
                {link.platform === 'spotify' && '♫ '}
                {link.platform === 'apple' && '🎙️ '}
                {link.platform === 'amazon' && '🎧 '}
                {link.platform === 'audible' && '📖 '}
                {link.label}
              </a>
            ))}
          </div>
        </nav>
      )}
    </header>
  );
}

/*
═══════════════════════════════════════════════════════════════════════════════════════
  COMPONENTS - Hero Section
═══════════════════════════════════════════════════════════════════════════════════════
*/

function HeroSection(): React.ReactElement {
  const { heroContent } = useHomepageState();

  return (
    <section className={styles.hero}>
      {/* Decorative stars background */}
      <div className={styles.starsBackground} aria-hidden="true">
        {[...Array(20)].map((_, i) => (
          <div
            key={i}
            className={styles.star}
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 3}s`,
              opacity: 0.3 + Math.random() * 0.4,
            }}
          />
        ))}
      </div>

      {/* Corner accents */}
      <div className={styles.cornerAccent} style={{ top: 40, left: 40 }} />
      <div className={styles.cornerAccent} style={{ top: 40, right: 40, transform: 'rotate(90deg)' }} />
      <div className={styles.cornerAccent} style={{ bottom: 40, left: 40, transform: 'rotate(-90deg)' }} />
      <div className={styles.cornerAccent} style={{ bottom: 40, right: 40, transform: 'rotate(180deg)' }} />

      <div className={styles.heroContent}>
        <h1 className={styles.heroHeadline}>
          <span className={styles.goldText}>{heroContent.headline}</span>
        </h1>
        <p className={styles.heroSubheadline}>{heroContent.subheadline}</p>
        <a href={heroContent.ctaLink} className={styles.ctaButton}>
          {heroContent.ctaText}
          <span className={styles.ctaArrow}>→</span>
        </a>
      </div>

      {/* Decorative separator */}
      <div className={styles.heroSeparator}>
        <div className={styles.separatorLine} />
        <div className={styles.separatorDiamond} />
        <div className={styles.separatorLine} />
      </div>
    </section>
  );
}

/*
═══════════════════════════════════════════════════════════════════════════════════════
  COMPONENTS - Features Section
═══════════════════════════════════════════════════════════════════════════════════════
*/

function FeatureCardComponent({ feature }: { feature: FeatureCard }): React.ReactElement {
  return (
    <div className={styles.featureCard}>
      <div className={styles.featureIcon}>{feature.icon}</div>
      <h3 className={styles.featureTitle}>{feature.title}</h3>
      <p className={styles.featureDescription}>{feature.description}</p>
    </div>
  );
}

function FeaturesSection(): React.ReactElement {
  const { features } = useHomepageState();

  return (
    <section className={styles.features} id="about">
      <h2 className={styles.sectionTitle}>
        Why <span className={styles.goldText}>B&A</span>?
      </h2>
      <div className={styles.featuresGrid}>
        {features.map((feature, index) => (
          <FeatureCardComponent key={index} feature={feature} />
        ))}
      </div>
    </section>
  );
}

/*
═══════════════════════════════════════════════════════════════════════════════════════
  COMPONENTS - Episodes Section
═══════════════════════════════════════════════════════════════════════════════════════
*/

function EpisodeCard({ episode }: { episode: Episode }): React.ReactElement {
  const cardContent = (
    <>
      <div className={styles.episodeThumbnail}>
        <img
          src={episode.thumbnailUrl}
          alt={episode.title}
          className={styles.thumbnailImage}
        />
        <div className={styles.thumbnailOverlay}>
          <span className={styles.thumbnailIcon}>▶</span>
        </div>
        <span className={styles.episodeDuration}>{episode.duration}</span>
      </div>
      <div className={styles.episodeContent}>
        <span className={styles.episodeCategory}>{episode.category}</span>
        <h3 className={styles.episodeTitle}>{episode.title}</h3>
        <p className={styles.episodeSubtitle}>{episode.subtitle}</p>
        <p className={styles.episodeDescription}>{episode.description}</p>
      </div>
    </>
  );

  if (episode.url) {
    return (
      <a
        href={episode.url}
        className={styles.episodeCard}
        target="_blank"
        rel="noopener noreferrer"
        style={{ textDecoration: 'none', color: 'inherit' }}
      >
        {cardContent}
      </a>
    );
  }

  return <article className={styles.episodeCard}>{cardContent}</article>;
}

function EpisodesSection(): React.ReactElement {
  const { featuredEpisodes } = useHomepageState();

  return (
    <section className={styles.episodes} id="episodes">
      <h2 className={styles.sectionTitle}>
        Featured <span className={styles.goldText}>Episodes</span>
      </h2>
      <div className={styles.episodesGrid}>
        {featuredEpisodes.map((episode) => (
          <EpisodeCard key={episode.id} episode={episode} />
        ))}
      </div>
      <div className={styles.episodesMore}>
        <a
          href="https://open.spotify.com/show/3ksIn68VJ0PY6RWwh3bAsZ"
          className={styles.moreLink}
          target="_blank"
          rel="noopener noreferrer"
        >
          All 25+ Episodes on Spotify <span>→</span>
        </a>
      </div>
    </section>
  );
}

/*
═══════════════════════════════════════════════════════════════════════════════════════
  COMPONENTS - Subscribe Section
═══════════════════════════════════════════════════════════════════════════════════════
*/

function SubscribeSection(): React.ReactElement {
  const { socialLinks } = useHomepageState();

  return (
    <section className={styles.subscribe} id="subscribe">
      <h2 className={styles.sectionTitle}>
        Listen <span className={styles.goldText}>Anywhere</span>
      </h2>
      <p className={styles.subscribeText}>
        Available on all major podcast platforms. Pick your favorite and subscribe.
      </p>
      <div className={styles.socialLinks}>
        {socialLinks.map((link) => (
          <a
            key={link.platform}
            href={link.url}
            className={styles.socialLink}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={link.label}
          >
            {link.platform === 'spotify' && '♫'}
            {link.platform === 'apple' && '🎙️'}
            {link.platform === 'amazon' && '🎧'}
            {link.platform === 'audible' && '📖'}
            <span className={styles.socialLinkLabel}>{link.label}</span>
          </a>
        ))}
      </div>
    </section>
  );
}

/*
═══════════════════════════════════════════════════════════════════════════════════════
  COMPONENTS - Footer
═══════════════════════════════════════════════════════════════════════════════════════
*/

function Footer(): React.ReactElement {
  return (
    <footer className={styles.footer}>
      <div className={styles.footerContent}>
        <div className={styles.footerLogo}>
          <span className={styles.goldText}>B&A</span>
        </div>
        <p className={styles.footerTagline}>Long-form narrative history</p>
        <div className={styles.footerContact}>
          <span className={styles.footerContactLabel}>Contact Us:</span>
          <a
            href="mailto:administration@boredandambitious.com"
            className={styles.footerContactEmail}
          >
            administration@boredandambitious.com
          </a>
        </div>
        <p className={styles.footerCopyright}>
          © {new Date().getFullYear()} Bored & Ambitious. All rights reserved.
        </p>
      </div>
    </footer>
  );
}

/*
═══════════════════════════════════════════════════════════════════════════════════════
  MAIN COMPONENT - Homepage Lobby Pod
═══════════════════════════════════════════════════════════════════════════════════════
*/

export function HomepageLobbyPod(): React.ReactElement {
  return (
    <div className={styles.page}>
      <Header />
      <main>
        <HeroSection />
        <FeaturesSection />
        <EpisodesSection />
        <SubscribeSection />
      </main>
      <Footer />
    </div>
  );
}